<script setup lang="ts">
import type { RadioGroupProps } from '@formwerk/core'
import { useFormContext, useRadioGroup } from '@formwerk/core'

interface RadioGroupFieldProps extends RadioGroupProps<string> {
  /**
   * Visually hides the label while keeping it accessible to screen readers.
   */
  hideLabel?: boolean
}

const props = defineProps<RadioGroupFieldProps>()

const { groupProps, labelProps, errorMessage, errorMessageProps, isBlurred }
  = useRadioGroup(props)

// Inside a form, errors only show after a submit attempt, then update live as the user fixes them
const form = useFormContext()
const errored = computed(() => (form ? form.isSubmitAttempted.value : isBlurred.value) && !!errorMessage.value)
</script>

<template>
  <div>
    <span
      v-if="label"
      v-bind="labelProps"
      class="typo-sans-1 mb-2 block uppercase"
      :class="{ 'sr-only': hideLabel }"
    >
      {{ label }}
    </span>

    <div
      v-bind="groupProps"
      class="flex flex-wrap gap-2"
      :class="{ 'c-red': errored }"
    >
      <slot />
    </div>

    <TransitionDefault>
      <p
        v-if="errored"
        v-bind="errorMessageProps"
        class="text-red-500 typo-sans-1 mt-1"
      >
        {{ errorMessage }}
      </p>
    </TransitionDefault>
  </div>
</template>
